import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, articleChapters } from '@/mocks/comics';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  const article = articles.find((a) => a.slug === slug);
  const chapters = article ? (articleChapters[article.id] || generateChapters(article)) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab('info');
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">漫画不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHelmet
        title={`${article.title} - 羞羞漫画 | ${article.author}作品`}
        description={article.description}
        keywords={`${article.title},羞羞漫画,${article.category},${article.tags.join(',')},漫画在线阅读`}
        canonicalUrl={`https://www.xiuxiumanhua.com/article/${slug}`}
        pageType="article"
        ogImage={article.coverImage}
        articleData={{
          publishedTime: '2026-01-01',
          modifiedTime: article.updateDate,
          author: article.author,
        }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.category, path: `/category/${article.categorySlug}` },
              { name: article.title },
            ]}
          />

          {/* Article Header */}
          <div className="flex flex-col md:flex-row gap-8 mb-10">
            <div className="flex-shrink-0 w-full md:w-64">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 羞羞漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-500">作者：{article.author}</span>
                <span className="text-xs text-background-50 bg-primary-500 px-2 py-0.5 rounded-full">
                  {article.status === 'serializing' ? '连载中' : '已完结'}
                </span>
                <span className="text-sm text-foreground-500">{article.category}</span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{article.rating}</strong>
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(0)}万阅读
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-heart-line mr-1"></i>{(article.favorites / 10000).toFixed(0)}万收藏
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-file-copy-line mr-1"></i>共{article.chapters}话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {article.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <button className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line mr-1"></i>加入收藏
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
            <button
              className={`px-6 py-2 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'info'
                  ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
              onClick={() => setActiveTab('info')}
            >
              作品介绍
            </button>
            <button
              className={`px-6 py-2 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'chapters'
                  ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
              onClick={() => setActiveTab('chapters')}
            >
              目录 ({chapters.length}话)
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'info' ? (
            <div className="bg-background-100 rounded-lg p-6 mb-10">
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content || '<p>暂无详细介绍</p>' }}
              />
            </div>
          ) : (
            <div className="bg-background-100 rounded-lg p-6 mb-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {chapters.map((ch) => (
                  <a
                    key={ch.id}
                    href="#"
                    className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.updateDate}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Related Comics */}
          <div className="mb-10">
            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">相关推荐</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
              {articles
                .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
                .slice(0, 4)
                .map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="group block"
                    data-product-shop
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="text-sm font-medium text-foreground-800 line-clamp-1">{comic.title}</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

function generateChapters(article: typeof articles[0]) {
  return Array.from({ length: Math.min(article.chapters, 30) }, (_, i) => ({
    id: `ch-gen-${article.id}-${i + 1}`,
    title: `第${i + 1}话`,
    number: i + 1,
    updateDate: article.updateDate,
    pages: Math.floor(Math.random() * 25) + 15,
  }));
}